import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Card,
  CardContent,
  Grid,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Description as AttestationIcon,
  PictureAsPdf as PdfIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { attestationsTravailAPI, employeesAPI, auditLogsAPI } from '../../../services/api';
import { AttestationTravail, AttestationTravailCreate } from '../../../types/attestationTravail';
import { Employee, EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import AlertDialog from '../../../components/AlertDialog';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { SlideUpTransition, useMinimizable, DialogControls, MinimizedBar } from '../../../components/MinimizableDialog';
// generateAttestationTravailPDF is dynamically imported in handleGeneratePDF
// so the jspdf chunk only loads when the user requests a PDF.

const HR_COLOR = '#00897B';

const ATT_TRAVAIL_FILTER_SCHEMA = {
  ref:       { type: 'text', param: 'ref' },
  date:      { type: 'dateRange', paramFrom: 'date_from', paramTo: 'date_to' },
  matricule: { type: 'text', param: 'matricule' },
  employe:   { type: 'text', param: 'employe' },
  poste:     { type: 'text', param: 'poste' },
  motif:     { type: 'text', param: 'motif' },
} as const;

const AttestationTravailManagement: React.FC = () => {
  const [attestations, setAttestations] = useState<AttestationTravail[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [anneeFilter, setAnneeFilter] = useState(new Date().getFullYear());

  // Form dialog state
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(formDialogOpen);
  const [formData, setFormData] = useState<{
    employee_id: number | null;
    date_attestation: string;
    motif: string;
  }>({
    employee_id: null,
    date_attestation: new Date().toISOString().split('T')[0],
    motif: '',
  });

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attestationToDelete, setAttestationToDelete] = useState<AttestationTravail | null>(null);

  // Employees for dropdown
  const [employees, setEmployees] = useState<EmployeeDropdownItem[]>([]);

  // Selected employee details
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState<Employee | null>(null);
  const [loadingEmployee, setLoadingEmployee] = useState(false);

  const { showSuccess, showError } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(ATT_TRAVAIL_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  const fetchAttestations = useCallback(async () => {
    setLoading(true);
    try {
      const extra = toQueryParams();
      const response = await attestationsTravailAPI.getAll({
        page: page + 1,
        size: rowsPerPage,
        search: search || undefined,
        annee: anneeFilter,
        extra: Object.keys(extra).length > 0 ? extra : undefined,
      });
      setAttestations(response.items);
      setTotalCount(response.total);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, anneeFilter, showError, filters]);

  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeesAPI.getAllForDropdown();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchAttestations();
  }, [fetchAttestations]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleAnneeChange = (event: SelectChangeEvent<number>) => {
    setAnneeFilter(event.target.value as number);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Cache an in-progress new attestation so an accidental backdrop-close doesn't wipe it.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
  }>('attestation-travail');

  const handleAddAttestation = () => {
    restore();
    const draft = loadDraft();
    if (draft) {
      setFormData(draft.formData);
      setSelectedEmployeeDetails(null);
      if (draft.formData.employee_id) {
        handleEmployeeSelect(draft.formData.employee_id);
      }
    } else {
      setFormData({
        employee_id: null,
        date_attestation: new Date().toISOString().split('T')[0],
        motif: '',
      });
      setSelectedEmployeeDetails(null);
    }
    setFormDialogOpen(true);
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') return;
    saveDraft({ formData });
    setFormDialogOpen(false);
  };

  // Annuler = deliberate cancel: discard any draft.
  const handleCancelForm = () => {
    clearDraft();
    setMinimized(false);
    setFormDialogOpen(false);
  };

  const handleEmployeeSelect = async (employeeId: number | null) => {
    setFormData(prev => ({ ...prev, employee_id: employeeId }));

    if (employeeId) {
      setLoadingEmployee(true);
      try {
        const employee = await employeesAPI.getById(employeeId);
        setSelectedEmployeeDetails(employee);
      } catch (error) {
        console.error('Error fetching employee details:', error);
        setSelectedEmployeeDetails(null);
      } finally {
        setLoadingEmployee(false);
      }
    } else {
      setSelectedEmployeeDetails(null);
    }
  };

  const handleFormSubmit = async () => {
    if (!formData.employee_id) {
      showError('Veuillez sélectionner un employé');
      return;
    }

    try {
      const createData: AttestationTravailCreate = {
        employee_id: formData.employee_id,
        date_attestation: formData.date_attestation,
        motif: formData.motif || undefined,
      };
      await attestationsTravailAPI.create(createData);
      showSuccess('Attestation créée avec succès');
      clearDraft();
      setFormDialogOpen(false);
      fetchAttestations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleDeleteClick = (attestation: AttestationTravail) => {
    setAttestationToDelete(attestation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!attestationToDelete) return;
    try {
      await attestationsTravailAPI.delete(attestationToDelete.id);
      showSuccess('Attestation supprimée avec succès');
      fetchAttestations();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setAttestationToDelete(null);
    }
  };

  const handleGeneratePDF = (attestation: AttestationTravail) => {
    openPreview({
      getBlob: async () => {
        const { generateAttestationTravailPDF } = await import('../../../utils/attestationTravailPdfGenerator');
        return generateAttestationTravailPDF(attestation, true) as Blob;
      },
      fileName: `attestation_travail_${attestation.ref_attestation || attestation.id}.pdf`,
    });
    auditLogsAPI.logPdfDownload('AttestationTravail', attestation.id, `Aperçu PDF - ${attestation.ref_attestation}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Generate year options (from 2020 to current year + 1)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);

  // Find selected employee for autocomplete
  const selectedEmployee = employees.find(e => e.id === formData.employee_id) || null;

  return (
    <Box sx={{ p: 0 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ bgcolor: HR_COLOR, color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttestationIcon />
                <Typography variant="h6">{totalCount}</Typography>
              </Box>
              <Typography variant="body2">Total Attestations</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Rechercher un employé..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Année</InputLabel>
            <Select value={anneeFilter} onChange={handleAnneeChange} label="Année">
              {yearOptions.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Actualiser">
            <IconButton onClick={fetchAttestations} sx={{ color: HR_COLOR }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddAttestation}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
          >
            Nouvelle Attestation
          </Button>
        </Box>

        {/* Table */}
        <TableContainer sx={getModuleTableTheme('hr')}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Réf.
                    <ColumnFilter type="text" value={filters.ref} onChange={(v) => update('ref', v)} label="référence" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Attestation
                    <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Matricule
                    <ColumnFilter type="text" value={filters.matricule} onChange={(v) => update('matricule', v)} label="matricule" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Employé
                    <ColumnFilter type="text" value={filters.employe} onChange={(v) => update('employe', v)} label="employé" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Poste
                    <ColumnFilter type="text" value={filters.poste} onChange={(v) => update('poste', v)} label="poste" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Motif
                    <ColumnFilter type="text" value={filters.motif} onChange={(v) => update('motif', v)} label="motif" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Chargement...</TableCell>
                </TableRow>
              ) : attestations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Aucune attestation trouvée</TableCell>
                </TableRow>
              ) : (
                attestations.map((attestation) => (
                  <TableRow key={attestation.id} hover>
                    <TableCell>
                      <Chip
                        label={attestation.ref_attestation}
                        size="small"
                        sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(attestation.date_attestation)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {attestation.employee?.matricule || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {attestation.employee?.nom_complet || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {attestation.employee?.fonction || '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {attestation.motif || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Générer PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleGeneratePDF(attestation)}
                            sx={{ color: '#1976D2' }}
                          >
                            <PdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(attestation)}
                            sx={{ color: '#EF5350' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Nouvelle Attestation de Travail
          <DialogControls color="white" onMinimize={minimize} onClose={handleCancelForm} />
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={employees}
              getOptionLabel={(option) => `${option.matricule} - ${option.nom_complet}`}
              value={selectedEmployee}
              onChange={(_, newValue) => {
                handleEmployeeSelect(newValue?.id || null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Employé"
                  required
                  fullWidth
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {/* Employee Info Card */}
            {loadingEmployee && (
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" color="text.secondary">
                  Chargement des informations...
                </Typography>
              </Paper>
            )}

            {selectedEmployeeDetails && !loadingEmployee && (
              <Paper sx={{ p: 2, bgcolor: '#E0F2F1', border: `1px solid ${HR_COLOR}` }}>
                <Typography variant="subtitle2" sx={{ color: HR_COLOR, mb: 1, fontWeight: 'bold' }}>
                  Informations Employé
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Matricule</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.matricule}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Nom Complet</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.nom_complet}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">CIN</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.cin || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Fonction</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.fonction || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Date Embauche</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {selectedEmployeeDetails.date_embauche ? formatDate(selectedEmployeeDetails.date_embauche) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Date Naissance</Typography>
                    <Typography variant="body2" fontWeight="500">
                      {selectedEmployeeDetails.date_naissance ? formatDate(selectedEmployeeDetails.date_naissance) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Type Contrat</Typography>
                    <Typography variant="body2" fontWeight="500">{selectedEmployeeDetails.type_contrat}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Statut</Typography>
                    <Chip
                      label={selectedEmployeeDetails.statut}
                      size="small"
                      sx={{
                        bgcolor: selectedEmployeeDetails.statut === 'ACTIF' ? '#66BB6A' : '#EF5350',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}

            <TextField
              label="Date Attestation"
              type="date"
              value={formData.date_attestation}
              onChange={(e) => setFormData(prev => ({ ...prev, date_attestation: e.target.value }))}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Motif (optionnel)"
              value={formData.motif}
              onChange={(e) => setFormData(prev => ({ ...prev, motif: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              placeholder="Ex: Pour servir et valoir ce que de droit"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelForm} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={formDialogOpen && minimized}
        color="#009688"
        title="Nouvelle Attestation de Travail"
        onRestore={restore}
        onClose={handleCancelForm}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        type="warning"
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer l'attestation ${attestationToDelete?.ref_attestation} de ${attestationToDelete?.employee?.nom_complet || 'cet employé'} ?`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        showCancel={true}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default AttestationTravailManagement;
